// src/components/Dashboard/PlayerDashboard/PlayerDashboard.jsx
// Main Player Dashboard - Personalized dashboard for players/athletes

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import { getUserEnrolledPrograms } from '../../../api/enrolledPrograms';
import { getAllUpcomingEvents } from '../../../api/upcomingEvents';
import { getUserAssignments } from '../../../api/myAssignments';
import ProgramSelectorBar from './ProgramSelectorBar';
import MyNextUpWidget from './MyNextUpWidget';
import WeekGlanceWidget from './WeekGlanceWidget';
import MyAssignmentsWidget from './MyAssignmentsWidget';
import MyApplicationsWidget from './MyApplicationsWidget';
import MyTeamsWidget from './MyTeamsWidget';
import PlayerStatsWidget from './PlayerStatsWidget';
import PlayerAwardsWidget from './PlayerAwardsWidget';
import AnnouncementsWidget from './AnnouncementsWidget';
import PendingActionsWidget from './PendingActionsWidget';
import MyClubsWidget from './MyClubsWidget';
import styles from './PlayerDashboard.module.css';

const STORAGE_KEY = 'playerDashboard_selectedProgramId';

const PlayerDashboard = ({
  userClubs,
  activeMode,
  onModeChange,
  onExploreClick
}) => {
  const { userProfile } = useUser();
  
  // State
  const [enrolledPrograms, setEnrolledPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch enrolled programs
  const fetchEnrolledPrograms = useCallback(async () => {
    if (!userClubs || !userProfile) {
      setEnrolledPrograms([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const programs = await getUserEnrolledPrograms(userClubs, userProfile);
      setEnrolledPrograms(programs);
      
      // Restore selected program from localStorage or default to "all"
      const storedProgramId = localStorage.getItem(STORAGE_KEY);
      if (storedProgramId && (storedProgramId === 'all' || programs.some(p => p._id === storedProgramId))) {
        setSelectedProgramId(storedProgramId);
      } else {
        setSelectedProgramId('all');
      }
    } catch (error) {
      console.error('Error fetching enrolled programs:', error);
      setEnrolledPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [userClubs, userProfile]);

  // Fetch upcoming events (ALL events, not just one per program)
  const fetchEvents = useCallback(async () => {
    if (!userClubs || !userProfile || userClubs.length === 0) {
      setUpcomingEvents([]);
      return;
    }

    try {
      const events = await getAllUpcomingEvents(userClubs, userProfile, 50);
      setUpcomingEvents(events);
    } catch (error) {
      console.error('Error fetching events:', error);
      setUpcomingEvents([]);
    }
  }, [userClubs, userProfile]);

  // Fetch assignments
  const fetchAssignments = useCallback(async () => {
    if (!userClubs || !userProfile || userClubs.length === 0) {
      setAssignments([]);
      return;
    }

    try {
      const data = await getUserAssignments(userClubs, userProfile, 20);
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignments([]);
    }
  }, [userClubs, userProfile]);

  // Initial fetch
  useEffect(() => {
    fetchEnrolledPrograms();
    fetchEvents();
    fetchAssignments();
  }, [fetchEnrolledPrograms, fetchEvents, fetchAssignments]);

  // Handle program selection
  const handleProgramSelect = (programId) => {
    setSelectedProgramId(programId);
    localStorage.setItem(STORAGE_KEY, programId);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchEnrolledPrograms(),
        fetchEvents(),
        fetchAssignments()
      ]);
      setRefreshTrigger(prev => prev + 1);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get selected program
  const selectedProgram = useMemo(() => {
    if (selectedProgramId === 'all') return null;
    return enrolledPrograms.find(p => p._id === selectedProgramId) || null;
  }, [enrolledPrograms, selectedProgramId]);

  // Filter events based on selected program
  const filteredEvents = useMemo(() => {
    if (selectedProgramId === 'all') return upcomingEvents;
    return upcomingEvents.filter(e => e.programId === selectedProgramId);
  }, [upcomingEvents, selectedProgramId]);

  // Filter assignments based on selected program
  const filteredAssignments = useMemo(() => {
    if (selectedProgramId === 'all') return assignments;
    return assignments.filter(a => a.programId === selectedProgramId);
  }, [assignments, selectedProgramId]);

  // Get clubs array from memberships
  const clubsArray = useMemo(() => {
    return userClubs?.map(m => m.club).filter(Boolean) || [];
  }, [userClubs]);

  // Get pending club applications
  const pendingClubApps = useMemo(() => {
    return userClubs?.filter(m => 
      m.status === 'pending' || m.status === 'Pending'
    ) || [];
  }, [userClubs]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // No enrollments - empty state
  if (enrolledPrograms.length === 0 && clubsArray.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>⚽</div>
          <h2>Welcome to Your Dashboard</h2>
          <p>You're not enrolled in any programs yet.</p>
          <p className={styles.emptyHint}>Join a club to get started with your athletic journey!</p>
          <button className={styles.exploreButton} onClick={onExploreClick}>
            Explore Clubs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header Bar */}
      <div className={styles.headerBar}>
        <div className={styles.headerRight}>
          <button
            className={`${styles.refreshButton} ${isRefreshing ? styles.refreshing : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh dashboard"
          >
            <FaSyncAlt />
          </button>
        </div>
      </div>

      {/* Program Selector */}
      {enrolledPrograms.length > 0 && (
        <ProgramSelectorBar
          programs={enrolledPrograms}
          selectedProgramId={selectedProgramId}
          onSelectProgram={handleProgramSelect}
        />
      )}

      {/* My Next Up - Hero Section */}
      <MyNextUpWidget
        events={filteredEvents}
        selectedProgram={selectedProgram}
        refreshTrigger={refreshTrigger}
      />

      {/* Week At a Glance */}
      <WeekGlanceWidget
        events={filteredEvents}
        programs={enrolledPrograms}
        selectedProgramId={selectedProgramId}
        refreshTrigger={refreshTrigger}
      />

      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* Primary Content - 2 columns wide on large screens */}
        <div className={styles.primaryColumn}>
          {/* My Assignments */}
          <MyAssignmentsWidget
            assignments={filteredAssignments}
            selectedProgram={selectedProgram}
            refreshTrigger={refreshTrigger}
          />
          
          {/* My Teams */}
          <MyTeamsWidget
            programs={enrolledPrograms}
            selectedProgramId={selectedProgramId}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
          />
          
          {/* Announcements - spans full width of primary column */}
          <div className={styles.fullSpan}>
            <AnnouncementsWidget
              programs={enrolledPrograms}
              selectedProgramId={selectedProgramId}
              refreshTrigger={refreshTrigger}
            />
          </div>
        </div>

        {/* Secondary Content - 1 column on right */}
        <div className={styles.secondaryColumn}>
          {/* My Applications */}
          <MyApplicationsWidget
            pendingClubApps={pendingClubApps}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
          />
          
          {/* Player Stats */}
          <PlayerStatsWidget
            programs={enrolledPrograms}
            events={upcomingEvents}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
          />
          
          {/* Player Awards */}
          <PlayerAwardsWidget
            programs={enrolledPrograms}
            selectedProgramId={selectedProgramId}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
          />
        </div>
      </div>

      {/* Pending Actions - Full Width */}
      <PendingActionsWidget
        pendingRsvps={filteredEvents.filter(e => !e.userRsvp || e.userRsvp === 'pending').length}
        pendingAssignments={filteredAssignments.length}
        pendingApps={pendingClubApps.length}
        refreshTrigger={refreshTrigger}
      />

      {/* My Clubs */}
      <MyClubsWidget
        clubs={clubsArray}
        enrolledPrograms={enrolledPrograms}
        onExploreClick={onExploreClick}
      />
    </div>
  );
};

export default PlayerDashboard;

